import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { sessionAPI } from '../../services/api';
import { FaCalendarAlt, FaArrowLeft, FaSave } from 'react-icons/fa';
import Button from '../../components/Button';

const CreateSession = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        start_date: '',
        end_date: '',
        max_applicants: '',
        status: 'upcoming'
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.start_date || !formData.end_date) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Prepare data - only send fields that exist in database
        const sessionData = {
            name: formData.name,
            start_date: formData.start_date,
            end_date: formData.end_date,
            status: formData.status,
            max_applicants: formData.max_applicants ? parseInt(formData.max_applicants) : null
        };

        try {
            setLoading(true);
            await sessionAPI.create(sessionData);
            toast.success('Session created successfully!');
            navigate('/admin/sessions');
        } catch (err) {
            console.error('Failed to create session:', err);
            toast.error(err.message || 'Failed to create session. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-3xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=sessions" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </Link>

                <div className="bg-white rounded-xl shadow-lg p-8">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-6">
                        <FaCalendarAlt className="text-primary-600" />
                        Create New Session
                    </h1>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Session Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g., 2024 Spring Cohort"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Start Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="start_date"
                                    value={formData.start_date}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    End Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="end_date"
                                    value={formData.end_date}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Max Applicants
                            </label>
                            <input
                                type="number"
                                name="max_applicants"
                                value={formData.max_applicants}
                                onChange={handleChange}
                                min="1"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Leave empty for unlimited"
                            />
                            <p className="text-xs text-gray-500 mt-1">Leave empty to allow unlimited applicants</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            >
                                <option value="upcoming">Upcoming</option>
                                <option value="open">Open (Students can apply)</option>
                                <option value="closed">Closed</option>
                                <option value="completed">Completed</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                                Select "Open" to allow students to apply for this session
                            </p>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <Button
                                type="submit"
                                variant="primary-gradient"
                                className="flex-1 px-6 py-3 text-gray-900 font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                                disabled={loading}
                            >
                                <FaSave className="inline mr-2" /> {loading ? 'Creating...' : 'Create Session'}
                            </Button>
                            <button
                                type="button"
                                onClick={() => navigate('/admin/sessions')}
                                className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateSession;

